import { Typography, useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { useRef, useState } from "react";
import MainButton from "../buttons/mainbutton";

type OnboardingUserPageModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export default function OnboardingUserPageModal({
  open,
  onClose,
  onConfirm,
}: OnboardingUserPageModalProps) {
  const mobileTheme = useTheme();
  const isDesktop = useMediaQuery(mobileTheme.breakpoints.down("sm"));
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const requestSaveFromIframe = async () => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) {
      return false;
    }

    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    return await new Promise<boolean>((resolve) => {
      const timeoutId = window.setTimeout(() => {
        window.removeEventListener("message", onMessage);
        resolve(false);
      }, 10000);

      function onMessage(event: MessageEvent) {
        const data = event.data;
        if (
          !data ||
          data.type !== "homeswap:onboarding-save-result" ||
          data.page !== "user" ||
          data.requestId !== requestId
        ) {
          return;
        }

        window.clearTimeout(timeoutId);
        window.removeEventListener("message", onMessage);
        resolve(Boolean(data.success));
      }

      window.addEventListener("message", onMessage);
      iframeWindow.postMessage(
        {
          type: "homeswap:onboarding-save-request",
          page: "user",
          requestId,
        },
        window.location.origin,
      );
    });
  };

  const handleContinue = async () => {
    setSaveError(null);
    setIsSaving(true);
    const didSave = await requestSaveFromIframe();
    setIsSaving(false);

    if (didSave) {
      onConfirm();
      return;
    }

    setSaveError("Could not save your profile yet. Please try again.");
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: !isDesktop ? "1400px" : "99vw",
          maxWidth: "1500px",
          height: "96vh",
          maxHeight: "96vh",
          bgcolor: "white",
          borderRadius: "16px",
          boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: "1px solid #e0e0e0" }}>
          <Typography variant="caption" sx={{ color: "#666" }}>
            Complete your profile details, then press Continue to save and move
            to the next step.
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, bgcolor: "#f8f8f8" }}>
          <iframe
            ref={iframeRef}
            src="/mypage?onboarding=1"
            title="My Page Onboarding"
            style={{
              width: "100%",
              height: "100%",
              border: 0,
              display: "block",
              backgroundColor: "white",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            px: 3,
            py: 1,
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <MainButton
            disabled={isSaving}
            isLoading={isSaving}
            onClick={handleContinue}
            text={"Continue"}
          />
        </Box>
        {saveError && (
          <Box sx={{ px: 3, pb: 1 }}>
            <Typography variant="caption" sx={{ color: "#d32f2f" }}>
              {saveError}
            </Typography>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
